/**
 * <Money> — 14-theme.md §6.4. The ONLY way a figure reaches the screen.
 *
 * Mono + tabular-nums + right-aligned come from `.cm-money` in tokens.css, so
 * there is no inline style and no `.ant-*` selector anywhere near it.
 */
import { formatMoney, formatQty, formatRate, formatPct, isNegative, type Money as MoneyValue } from '@/lib/money';

export type MoneyVariant = 'base' | 'strong' | 'total' | 'net';

export interface MoneyProps {
  value: MoneyValue;
  variant?: MoneyVariant;
  /** `formatRate` keeps up to 6 dp — use it for rate-card figures only. */
  as?: 'money' | 'rate' | 'qty' | 'pct';
  /**
   * `false` drops the `RM` prefix and nothing else. Only for a grid whose every
   * cell is money and which names the unit once in its caption (the daily
   * earnings report) — see `FormatMoneyOptions` in `lib/money.ts`.
   */
  currency?: boolean;
  className?: string;
  title?: string;
}

export function Money({
  value,
  variant = 'base',
  as = 'money',
  currency = true,
  className,
  title,
}: MoneyProps) {
  const absent = value === null || value === undefined || value === '';
  const text =
    as === 'rate'
      ? formatRate(value, { currency })
      : as === 'qty'
        ? formatQty(value)
        : as === 'pct'
          ? formatPct(value)
          : formatMoney(value, { currency });

  const cls = [
    'cm-money',
    variant !== 'base' && `cm-money--${variant}`,
    absent && 'cm-money--absent',
    !absent && isNegative(value) && 'cm-money--negative',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={cls} title={title}>
      {text}
    </span>
  );
}

/** A quantity: no currency, up to 3 dp, still mono and tabular. */
export function Qty({ value, className }: { value: MoneyValue; className?: string }) {
  return <Money value={value} as="qty" className={className} />;
}

/** An identifier / work-type code / job-paper ref — `.cm-code`. */
export function Code({ children, strong }: { children: React.ReactNode; strong?: boolean }) {
  return <span className={strong ? 'cm-code cm-code--strong' : 'cm-code'}>{children}</span>;
}
